import { jsx as _jsx } from "react/jsx-runtime";
import { useState, useCallback, useMemo, useEffect, useRef, useTransition } from 'react';
import { PdfParagraphsContext } from '../index.js';
export const PdfParagraphsProvider = ({ identity, children, layoutService, }) => {
    const [cache, setCache] = useState(new Map());
    const [paragraphMapCache, setParagraphMapCache] = useState(new Map());
    const [isPending, startTransition] = useTransition();
    // --- START: Buffer and scheduling for streaming updates ---
    const paragraphBuffer = useRef(new Map());
    const updateScheduled = useRef(false);
    // --- END: Buffer and scheduling for streaming updates ---
    const pendingPagesRef = useRef(new Set());
    const isPausedRef = useRef(false);
    // Reset cache and buffers when the document identity changes.
    useEffect(() => {
        setCache(new Map());
        setParagraphMapCache(new Map());
        paragraphBuffer.current.clear();
        updateScheduled.current = false;
        pendingPagesRef.current.clear();
    }, [identity]);
    const prefetchPages = useCallback((pageNumbers) => {
        if (isPausedRef.current) {
            return;
        }
        const { id: documentId, type } = identity;
        if (type !== 'pdf')
            return;
        const pagesToFetch = pageNumbers.filter(pn => !cache.has(pn) && !pendingPagesRef.current.has(pn));
        if (pagesToFetch.length === 0)
            return;
        pagesToFetch.forEach(pn => pendingPagesRef.current.add(pn));
        // This function processes the buffer and updates the state.
        // It's called by requestAnimationFrame to throttle UI updates.
        const processBuffer = () => {
            if (paragraphBuffer.current.size === 0) {
                updateScheduled.current = false;
                return;
            }
            const bufferToProcess = new Map(paragraphBuffer.current);
            paragraphBuffer.current.clear();
            startTransition(() => {
                setCache(prevCache => {
                    const newCache = new Map(prevCache);
                    for (const [pageNum, paragraphs] of bufferToProcess.entries()) {
                        const existing = newCache.get(pageNum) ?? [];
                        newCache.set(pageNum, [...existing, ...paragraphs]);
                    }
                    return newCache;
                });
                setParagraphMapCache(prevMap => {
                    const newMap = new Map(prevMap);
                    for (const paragraphs of bufferToProcess.values()) {
                        for (const p of paragraphs) {
                            newMap.set(p.paragraphId, p);
                        }
                    }
                    return newMap;
                });
            });
            updateScheduled.current = false;
        };
        const callbacks = {
            onParagraphBatchReady: (pageNumber, batch) => {
                // Add received batch to the buffer.
                if (!paragraphBuffer.current.has(pageNumber)) {
                    paragraphBuffer.current.set(pageNumber, []);
                }
                paragraphBuffer.current.get(pageNumber).push(...batch);
                // Schedule an update if not already scheduled.
                if (!updateScheduled.current) {
                    updateScheduled.current = true;
                    requestAnimationFrame(processBuffer);
                }
            },
            onPageDone: pageNumber => {
                // While rAF handles batching, we still need to mark pages as not pending.
                // This doesn't trigger a render, just internal state management.
                pendingPagesRef.current.delete(pageNumber);
            },
            onComplete: () => {
                // When all pages are processed, ensure any lingering data in the buffer is flushed.
                if (paragraphBuffer.current.size > 0) {
                    processBuffer();
                }
                // The original logic to clear pending pages is now mostly handled by onPageDone,
                // but we can clear the whole set as a final cleanup.
                pendingPagesRef.current.clear();
            },
            onError: error => {
                console.error('[Layout Service] Failed to get layout for pages:', pagesToFetch, error);
                pagesToFetch.forEach(pn => pendingPagesRef.current.delete(pn));
                // Also clear any buffered data that won't be used.
                paragraphBuffer.current.clear();
                updateScheduled.current = false;
            },
        };
        layoutService.layoutPages(documentId, pagesToFetch, callbacks);
    }, [identity, layoutService, startTransition, cache]);
    const getCachedParagraphsForPage = useCallback((pageNumber) => {
        return cache.get(pageNumber);
    }, [cache]);
    const pauseRenderQueue = useCallback(() => {
        isPausedRef.current = true;
    }, []);
    const resumeRenderQueue = useCallback(() => {
        isPausedRef.current = false;
    }, []);
    const contextValue = useMemo(() => ({
        // Provide a compatible API for existing consumers.
        getParagraphsForPage: async (pn) => cache.get(pn) || [],
        getCachedParagraphsForPage,
        prefetchPagesInBatch: prefetchPages,
        prefetchParagraphsForPage: (pn) => prefetchPages([pn]),
        paragraphsByPage: cache,
        paragraphMap: paragraphMapCache,
        isBatchLoading: isPending,
        pauseRenderQueue,
        resumeRenderQueue,
    }), [
        getCachedParagraphsForPage,
        prefetchPages,
        cache,
        paragraphMapCache,
        isPending,
        pauseRenderQueue,
        resumeRenderQueue,
    ]);
    return (_jsx(PdfParagraphsContext.Provider, { value: contextValue, children: children }));
};
//# sourceMappingURL=PdfParagraphsProvider.js.map